// Small pure helpers shared by the Admin › Wallets pages.

import { compareMoney, sumMoney as sumMoneyExact } from '../../../utils/money';
import type { AdminWalletDeposit } from '../../../types/models/Wallet';

const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Strict `YYYY-MM-DD` parser for the DatePickers (via `dateParse`) and the filter commit gates.
 * JS Date APIs roll invalid calendar dates over instead of rejecting them (`Date.parse('2026-02-31')`
 * is 2026-03-03), so a round-trip check is required — mirrors `Invoices.tsx`.
 */
export function parseStrictIsoDate(value: string): Date {
  if (!ISO_DATE_RE.test(value)) {
    return new Date(NaN);
  }
  const [year, month, day] = value.split('-').map(Number);
  const parsed = new Date(year, month - 1, day);
  const roundTrips = parsed.getFullYear() === year && parsed.getMonth() === month - 1 && parsed.getDate() === day;
  return roundTrips ? parsed : new Date(NaN);
}

/** A DatePicker's raw value becomes an API date only once it is a complete, real `YYYY-MM-DD`. */
export function toApiDate(value: string): string | undefined {
  return Number.isNaN(parseStrictIsoDate(value).getTime()) ? undefined : value;
}

/** Sum of 2-decimal money strings — DISPLAY ONLY (never sent back). Unparseable values count as 0. */
export function sumMoney(values: string[]): number {
  return values.reduce((acc, v) => {
    const n = parseFloat(v);
    return Number.isNaN(n) ? acc : acc + n;
  }, 0);
}

/** Signed amount accepted by the adjustment form: optional sign, ≤7 integer digits, ≤2 decimals (comma or dot). */
export const SIGNED_AMOUNT_RE = /^[+-]?\d{1,7}([.,]\d{1,2})?$/;

/**
 * Normalise a typed signed amount to the wire shape: comma → dot, a leading `+` dropped, the `-`
 * kept, always 2 decimals (`"-25"` → `"-25.00"`, `"40,5"` → `"40.50"`). Returns `null` when the
 * input is malformed or zero.
 */
export function normaliseSignedAmount(raw: string): string | null {
  const trimmed = raw.trim();
  if (!SIGNED_AMOUNT_RE.test(trimmed)) {
    return null;
  }
  const negative = trimmed.startsWith('-');
  const unsigned = trimmed.replace(/^[+-]/, '').replace(',', '.');
  const [intPart, fracPart = ''] = unsigned.split('.');
  const normalised = `${intPart}.${fracPart.padEnd(2, '0')}`;
  if (parseFloat(normalised) === 0) {
    return null;
  }
  return negative ? `-${normalised}` : normalised;
}

/** A `settling` wallet invoice payment older than this is a request that died mid-flight — resolvable. */
export const STALE_SETTLING_MS = 10 * 60 * 1000;

/**
 * Whether staff may Resolve a wallet invoice payment (docs/api-types/admin-wallets.md): a parked
 * `needs_reconciliation` row always; a `settling` row only once it is older than 10 minutes (the
 * backend refuses younger ones — "Only a payment awaiting reconciliation (or a settling attempt
 * older than 10 minutes) can be resolved"); an unlinked saved-card claim (`source: card`, no Mollie
 * payment id) at any age. `created_at` is the backend's `YYYY-MM-DD HH:MM:SS` string; an unparseable
 * or missing value is treated as not stale.
 */
export function isResolvableInvoicePayment(
  payment: { status: string; created_at: string | null; source?: string; mollie_payment_id?: string | null },
  now: number = Date.now(),
): boolean {
  if (payment.status === 'needs_reconciliation') {
    return true;
  }
  // An unlinked saved-card claim (the create response was lost): resolvable at any age — the
  // backend answers `pending` while it is younger than 10 minutes, `released`/`paid` after.
  if (payment.source === 'card' && (payment.mollie_payment_id ?? null) === null) {
    return payment.status === 'settling';
  }
  if (payment.status !== 'settling' || payment.created_at === null) {
    return false;
  }
  // Safari rejects the space-separated form; the T-form parses everywhere.
  const ts = Date.parse(payment.created_at.replace(' ', 'T'));
  return !Number.isNaN(ts) && now - ts > STALE_SETTLING_MS;
}

/** Positive amount accepted by the refund form: ≤7 integer digits, ≤2 decimals (comma or dot), no sign. */
const POSITIVE_AMOUNT_RE = /^\d{1,7}([.,]\d{1,2})?$/;

/**
 * Normalise a typed positive amount to the wire shape (`"40"` → `"40.00"`, `"12,5"` → `"12.50"`).
 * Returns `null` when the input is malformed, signed or zero.
 */
export function normalisePositiveAmount(raw: string): string | null {
  const trimmed = raw.trim();
  return POSITIVE_AMOUNT_RE.test(trimmed) ? normaliseSignedAmount(trimmed) : null;
}

/** What is left to refund on a top-up: `amount − clawback_amount`, exact (integer cents); null when unreadable. */
export function refundableRemainder(deposit: Pick<AdminWalletDeposit, 'amount' | 'clawback_amount'>): string | null {
  return sumMoneyExact([deposit.amount, `-${deposit.clawback_amount}`.replace(/^--/, '')]);
}

/**
 * Whether staff may Refund a top-up (docs/api-types/admin-wallets.md): it is credited, has a Mollie
 * payment, and something is left to refund. The backend re-checks everything, live at Mollie.
 */
export function isRefundableDeposit(deposit: AdminWalletDeposit): boolean {
  if (!deposit.credited || !deposit.mollie_payment_id || typeof deposit.clawback_amount !== 'string') {
    return false;
  }
  const remainder = refundableRemainder(deposit);
  return remainder !== null && (compareMoney(remainder, '0.00') ?? 0) > 0;
}

/** How the refund form reacts to a refusal (the backend's messages are documented, in check order). */
export interface RefundRefusal {
  /** Staff guidance shown under the backend's message, or null when the message says it all. */
  hint: string | null;
  /** The top-up changed (a resubmission, or a stale page): reload, and never re-post from this form. */
  stale: boolean;
  /** Suggest the « Record as receivable » checkbox. */
  suggestReceivable: boolean;
}

export function refundRefusal(message: string): RefundRefusal {
  const base: RefundRefusal = { hint: null, stale: false, suggestReceivable: false };
  if (message.startsWith('The top-up changed meanwhile') || message === 'Top-up not found') {
    return {
      ...base,
      stale: true,
      hint: 'Nothing was recorded. The page has been reloaded: close this dialog and check the top-up again before refunding.',
    };
  }
  if (message.startsWith('Mollie offers no refund on this top-up payment')) {
    return {
      ...base,
      hint: 'This payment method (e.g. paysafecard, gift card) cannot be refunded at Mollie. Return the money another way (a bank transfer) and record it with Adjust as a negative adjustment.',
    };
  }
  if (message.startsWith('Mollie already reports a refund or chargeback on this top-up')) {
    return {
      ...base,
      hint: 'A refund or chargeback was already made at Mollie: record nothing here. Its webhook will debit the wallet — wait a moment, then reload the page.',
    };
  }
  if (message.startsWith('Mollie cannot refund that much on this payment')) {
    return { ...base, hint: 'Lower the amount to what Mollie can still refund on this payment.' };
  }
  if (message.startsWith('The balance does not cover this refund')) {
    return {
      ...base,
      suggestReceivable: true,
      hint: 'The customer has already spent part of this money. Tick « Record as receivable » to let the balance go negative.',
    };
  }
  if (message.startsWith('Could not check this top-up at Mollie')) {
    return { ...base, hint: 'Nothing was recorded. Try again in a moment.' };
  }
  return base;
}

/**
 * Whether staff may Cancel a recorded refund on a top-up: credited, paid through Mollie, and something was
 * taken back. How much can be cancelled depends on Mollie (the backend's floor): it answers the maximum.
 */
export function isCancellableDeposit(deposit: AdminWalletDeposit): boolean {
  return (
    deposit.credited &&
    !!deposit.mollie_payment_id &&
    typeof deposit.clawback_amount === 'string' &&
    (compareMoney(deposit.clawback_amount, '0.00') ?? 0) > 0
  );
}

/** How the cancel-refund form reacts to a refusal (documented messages, in check order). */
export function cancelRefusal(message: string): RefundRefusal {
  const base: RefundRefusal = { hint: null, stale: false, suggestReceivable: false };
  if (message.startsWith('The top-up changed meanwhile') || message === 'Top-up not found') {
    return {
      ...base,
      stale: true,
      hint: 'Nothing was recorded. The page has been reloaded: close this dialog and check the top-up again.',
    };
  }
  if (message.startsWith('Could not check this top-up at Mollie')) {
    return { ...base, hint: 'Nothing was recorded. Try again in a moment.' };
  }
  if (message.startsWith('Mollie returned an unexpected state for this top-up payment')) {
    return { ...base, hint: 'Nothing was recorded. Look at the payment in the Mollie dashboard before going further.' };
  }
  return base;
}

/**
 * The guidance under the backend's « cancellable maximum » field error: at 0.00 EUR nothing can be cancelled
 * yet, because Mollie has refunded, or may still refund, that money.
 */
export function cancellableMaximumHint(fieldError: string): string | null {
  const match = /cancellable maximum of this top-up \(([\d.]+) EUR\)/.exec(fieldError);
  if (match === null) {
    return null;
  }
  return match[1] === '0.00'
    ? 'Nothing can be cancelled now: Mollie has refunded, or may still refund, this money (a refund still queued, pending or processing there). Wait for it, or cancel a queued or pending refund in the Mollie dashboard first.'
    : `Enter at most ${match[1]} EUR.`;
}
